<template>
    <div class="container-table2 w-100">
        <div class="table-responsive">
            <b-skeleton-wrapper :loading="isLoading">
                <template #loading>
                    <b-skeleton-table
                        :rows="10"
                        :columns="columns.length"
                        :table-props="{ bordered: true, striped: true }"
                    />
                </template>
                <table>
                    <thead>
                        <tr>
                            <th v-for="column in columns" :key="column.fieldName">
                                <div class="d-flex align-items-center" style="gap: 2px;" :id="column">
                                    <span>{{ column.name ?? $t(`shared.field_names.${column.fieldName}`) }}</span>
                                    <b-button v-b-tooltip.hover  class="btn-transparent" v-if="column.tooltip" :title="column.tooltip">
                                        <img                         
                                            src="@/assets/img/icons/information-circle-gray.svg"
                                            class="info-icon"
                                            alt=""
                                        />
                                    </b-button>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, index) in rows" :key="index" v-if="rows.length > 0">
                          <td v-for="column in columns" :key="column.fieldName">
                            <template v-if="$scopedSlots[`cell-${column.fieldName}`]">
                              <slot :name="`cell-${column.fieldName}`" :row="row" :value="row[column.fieldName]" />
                            </template>
                            <template v-else>
                              <span
                                v-if="column.type === 'status'"
                                :class="getBadge(row[column.fieldName])"
                              >
                                {{ column.renderText ? column.renderText(row[column.fieldName], row) : getStatusText(row[column.fieldName]) }}
                              </span>
                              <template v-else-if="!column.type && column.render">
                                <div v-html="column.render(row[column.fieldName], row)"></div>
                              </template>
                              <span v-else-if="column.type === 'phone'">{{ formatPhoneNumber(row[column.fieldName]) }}</span>
                              <span v-else-if="column.type === 'payment_method'">{{ getPaymentMethodName(row[column.fieldName]) }}</span>
                              <span v-else-if="column.type === 'link'" class="link-table" @click="openUrl(column.path, row[column.fieldName])">{{ row[column.fieldName] }}</span>
                              <span v-else-if="column.type === 'icon'" class="table-body-action" @click="$emit('getQuickView', row)">
                                <img src="@/assets/img/icons/eye.svg" alt="eye icon" />
                              </span>
                              <span v-else-if="column.type === 'date'">
                                {{ formatDate(row[column.fieldName], column.format) }}
                              </span>
                              <span v-else>{{ row[column.fieldName] }}</span>
                            </template>
                          </td>
                        </tr>
                        <tr style="border-width: 0;" v-if="!rows || rows.length == 0">
                            <td :colspan="columns.length" class="no-data">Não existem dados disponíveis.</td>
                        </tr>
                    </tbody>
                </table>
            </b-skeleton-wrapper>
            
        </div>
    </div>
</template>

<script>
import moment from "moment/moment";

export default {
    name: "TableReport",
    props: {
        filter: Array,
        columns: Array,
        rows: Array,
        isLoading: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            status: '',
            pagination: {
                totalPages: 1,
                currentPage: 1,
                perPage: 10,
            },
            paymentMethods: [
                { id: "CREDIT_CARD", name: "Cartão de crédito" },
                { id: "BOLETO", name: "Boleto" },
                { id: "PIX", name: "Pix" },
                { id: "PAYPAL", name: "PayPal" },
                { id: "SAFETYPAY-CASH", name: "SAFETYPAY-CASH" },
                { id: "OXXO", name: "OXXO" },
                { id: "EFECTY", name: "EFECTY" },
                { id: "DEBITCARD", name: "Cartão de débito" },
                { id: "EFT", name: "EFT" },
                { id: "SPEI", name: "SPEI" },
                { id: "SENCILLITO", name: "SENCILLITO" }
            ]
        }
    },
    methods: {
        formatDate(value, $format = 'DD/MM/YYYY') {
            if (value) {
              const date = moment(value);
              return date.isValid() ? date.format($format) : '-';
            }
            return '-';
        },
        formatPhoneNumber(phone) {
            if (!phone) return '';
            return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        },
        openUrl(path, id) {
            if (path.lastIndexOf('=') === -1) {
                path += '=';
            }
            window.open(path + id, '_blank');
        },
        getPaymentMethodName(methodId) {
            if (!methodId) return '';
            
            const upperMethodId = methodId.toUpperCase();
            
            const method = this.paymentMethods.find(m => m.id === upperMethodId);
            
            return method ? method.name : methodId;
        },
        getStatusText(status) {
            if (!status) return '';
            
            switch (status.toLowerCase()) {
                case 'processing': 
                    return this.$t('shared.status.processing');
                case 'authorized':
                    return this.$t('shared.status.authorized');
                case 'paid':
                    return this.$t('shared.status.paid');
                case 'canceled':
                    return this.$t('shared.status.canceled');
                case 'ended':
                    return this.$t('shared.status.ended');
                case 'refunded':
                    return this.$t('shared.status.refunded');
                case 'waiting_payment':
                    return this.$t('shared.status.waiting_payment');
                case 'pending_payment':
                    return this.$t('shared.status.pending_payment');
                case 'refund_pending':
                    return this.$t('shared.status.refund_pending');
                case 'pending_refund':
                    return this.$t('shared.status.pending_refund');
                case 'refused':
                    return this.$t('shared.status.refused');
                case 'chargedback':
                    return this.$t('shared.status.chargedback');
                case 'delayed':
                    return this.$t('shared.status.delayed');
                case 'trialing':
                    return this.$t('shared.status.trialing');
                case 'unpaid':
                    return this.$t('shared.status.unpaid');
                default:
                    return status;
            }
        },
        getBadge(status) {
            if (!status || status === '-') return '';
            
            const statusMap = {
                'processing': 'badge badge-processing',
                'authorized': 'badge badge-paid',
                'paid': 'badge badge-paid',
                'canceled': 'badge badge-canceled',
                'ended': 'badge badge-ended',
                'refunded': 'badge badge-refunded',
                'waiting_payment': 'badge badge-waiting_payment',
                'pending_payment': 'badge badge-pending_payment',
                'refund_pending': 'badge badge-refund_pending',
                'pending_refund': 'badge badge-pending_refund',
                'refused': 'badge badge-refused',
                'chargedback': 'badge badge-chargedback',
                'delayed': 'badge badge-delayed',
                'trialing': 'badge badge-trialing',
                'unpaid': 'badge badge-unpaid',
            };
            return statusMap[status.toLowerCase()] || '';
        }
    }
};
</script>

<style scoped>
    .no-data {
        color: #4B5563;
        text-align: center;  
        padding: 44px 0px 44px 0px;
        font-family: Inter;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
    }
    .container-table2 {
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
    }
    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }
    .link-table {
        color: var(--Default-primary-800, #0854C5);

        font-family: Inter;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
        text-decoration: none;
    }

    .link-table:hover {
        text-decoration: underline;
        cursor: pointer;
    }
    table {
        width: 100%;
        border-collapse: collapse;
    }
    th {
        background-color: #F3F4F6;
        color: #475467;
        font-size: 13px;
        line-height: 18px;
        font-family: Inter;
        font-weight: 500;
        text-align: left;
        white-space: normal;
    }
    th, td {
        padding: 8px 8px;
        border: none;
    }
    td {
        font-family: Inter;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
    }

    td:has(.table-body-action) {
        text-align: center;
    }

    .table-body-action {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .table-body-action img {
        width: 20px;
        height: 20px;
    }

    tr:not(:last-child) {
        border-bottom: 1px solid #EAECF0;
    }

    

    /* Badge */
    .badge {
        height: 25px;
        border-radius: 16px;
        padding: 8px 12px 8px 12px;
    }

    .badge-paid {
        background: #D1FAE5;
        color: #065F46
    }

    .badge-processing {
        background: #D6F0FF;
        color: #0854C5;
    }

    .badge-authorized {
        background: #D6F0FF;
        color: #0854C5;
    }

    .badge-refused {
        background: #FEE2E2;
        color: #92400E;
    }

    .badge-unpaid {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-refunded {
        background: #F3F4F6;
        color: #1F2937;
    }

    .badge-chargedback {
        background: #D1FAE5;
        color: #000000
    }

    .badge-canceled {
        background: #D1FAE5;
        color: #000000
    }

    .badge-ended {
        background: #F2F4F7;
        color: #344054;
    }

    .badge-trialing {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-waiting_payment {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-pending_payment {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-refund_pending {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-canceled {
        background: #FEE4E2;
        color: #B42318;
    }

    .badge-ended {
        background: #F2F4F7;
        color: #344054;
    }

    .badge-unpaid {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-delayed {
        background: #FEF3C7;
        color: #92400E;
    }

    .badge-created {
        background: #D6F0FF;
        color: #0854C5;
    }
    .btn-transparent {
      background-color: transparent;
      border: 1px solid transparent;
      padding: 0;
      width: auto;
      height: auto;
    }
    
    .btn-transparent:hover {
        background-color: transparent;
        border: 1px solid transparent;
    }

</style>
